// ── The one body lock, for both window shells (FS-10; batch 12 tail T2, task 2b) ───────────────────────────────
// Moved here out of CompanyWorkspace/dialog.tsx (which re-exports it) so MerchantDoc/shell.tsx's full-screen DocWindow
// locks the page with the SAME lock as the workspace's Dialog — two locks would each restore what they captured, and
// undo each other.

/** FS-10, the half of the body lock people forget. Locking the page behind the window is easy; giving the operator
 *  their place back afterwards is the part that matters, because a long list scrolled back to the top is worse than a
 *  page that was never locked.
 *
 *  Returns the unlock (calling it twice unlocks once).
 *
 *  ⛔ COUNTED, not a stack of snapshots. Each lock used to capture the body's styles and offset at its own call and
 *  restore exactly those, which is only right when windows close in REVERSE order. Two staff document windows can be
 *  open at once (Sales: New under an Edit opened by keyboard — panes/Documents.tsx `doneCreate` / `doneEdit` close only
 *  their own), so New (A) then Edit (B), then A saved first, left `overflow` cleared under B — and B's close then
 *  restored the `hidden` it had captured, locking the page for good. Now the FIRST lock captures the styles and the
 *  offset (BEFORE touching anything) and sets them, later locks only count, and the LAST unlock restores what the first
 *  captured — whatever order the windows close in.
 *
 *  ⛔ Why a measured padding and not `scrollbar-gutter: stable`. Hiding the body's overflow removes the scrollbar, the
 *  content reflows into the freed width, and the page jumps sideways under the operator. `scrollbar-gutter` is the
 *  modern fix, but it has to apply to the VIEWPORT's scroll container, and whether the property propagates from `body`
 *  the way `overflow` does is engine-dependent — so setting it here can silently keep the jump. `window.innerWidth -
 *  documentElement.clientWidth`, measured while the scrollbar is still there, IS the width the scrollbar occupied:
 *  exact, universally supported, no propagation question. */
let held = 0;
let saved: { x: number; y: number; overflow: string; pad: string } | null = null;

export function lockBodyScroll(): () => void {
  const body = document.body;
  if (held === 0) {
    saved = { x: window.scrollX || 0, y: window.scrollY || 0, overflow: body.style.overflow, pad: body.style.paddingRight };
    const bar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    body.style.overflow = 'hidden';
    if (bar > 0) body.style.paddingRight = `${bar}px`;
  }
  held++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    held = Math.max(0, held - 1);
    if (held > 0 || !saved) return;
    const s = saved;
    saved = null;
    // ⛔ restore means REMOVE when there was no declaration to begin with. Assigning '' is supposed to do that and
    // mostly does, but it is silently ignored for some properties by some CSSOM implementations — and a
    // `padding-right: 15px` left on the body after the window closes is a visible gap down the side of every page.
    // removeProperty is unambiguous everywhere.
    const put = (prop: string, val: string) => { if (val) body.style.setProperty(prop, val); else body.style.removeProperty(prop); };
    put('overflow', s.overflow);
    put('padding-right', s.pad);
    window.scrollTo(s.x, s.y);
  };
}
